"use client";
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Database, 
  Globe, 
  Shield, 
  Users, 
  Zap, 
  CheckCircle, 
  Search, 
  ArrowRight,
  ExternalLink,
  Info
} from 'lucide-react';
import { Boxes } from '../ui/background-boxes';
import { HoverBorderGradient } from '../ui/hover-border-gradient';
import { Modal } from '../ui/Modal';

const SecurityStory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedStage, setSelectedStage] = useState(null);

  // Scroll to top on location change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Navigation handlers
  const handleLearnMore = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const handleContactSales = () => {
    navigate('/contact');
    window.scrollTo(0, 0);
  };

  const handleGetDemo = () => {
    navigate('/contact');
    window.scrollTo(0, 0);
  };

  const stageDetails = {
    "Where is my data?": {
      sections: [
        {
          title: "Complete data source visibility",
          description: "Our platform provides comprehensive visibility into all your data sources across your entire IT infrastructure.",
          points: [
            "Real-time data source discovery and mapping",
            "Automated asset inventory and classification",
            "Visual data flow mapping and dependencies"
          ]
        },
        {
          title: "IT landscape integration",
          description: "Seamlessly integrate with your existing IT infrastructure to provide a unified view of your data ecosystem.",
          points: [
            "Native integration with major cloud providers",
            "Support for hybrid and multi-cloud environments",
            "Seamless integration with existing security tools"
          ]
        },
        {
          title: "Real-time data tracking",
          description: "Monitor and track your data in real-time to ensure complete visibility and control.",
          points: [
            "Continuous data movement monitoring",
            "Real-time alerts on suspicious activities",
            "Historical data tracking and audit trails"
          ]
        }
      ]
    },
    "What is my data?": {
      sections: [
        {
          title: "PII data identification",
          description: "Advanced algorithms to detect and classify personally identifiable information across your data sources.",
          points: [
            "AI-powered PII detection",
            "Custom PII pattern matching",
            "Regulatory compliance mapping"
          ]
        },
        {
          title: "Credit card information protection",
          description: "Specialized protection for payment card information and financial data.",
          points: [
            "PCI DSS compliance monitoring",
            "Tokenization and encryption",
            "Access control and audit logging"
          ]
        },
        {
          title: "PHI data protection",
          description: "Advanced protection and compliance for sensitive healthcare data and medical records.",
          points: [
            "HIPAA compliance monitoring",
            "Medical record classification",
            "Healthcare data protection"
          ]
        }
      ]
    },
    "Who has access to my data?": {
      sections: [
        {
          title: "SaaS application access control",
          description: "Granular control over who can access your data across SaaS application.",
          points: [
            "Role-based access control",
            "Just-in-time access provisioning",
            "Access review automation"
          ]
        },
        {
          title: "Cloud instance monitoring",
          description: "Continuous monitoring of cloud resources and access patterns.",
          points: [
            "Real-time access monitoring",
            "Anomaly detection",
            "Resource usage tracking"
          ]
        },
        {
          title: "Security team oversight",
          description: "Empower security teams with comprehensive visibility and control.",
          points: [
            "Centralized security dashboard",
            "Automated compliance reporting",
            "Incident response automation"
          ]
        }
      ]
    },
    "Is my data properly protected?": {
      sections: [
        {
          title: "Industry-specific compliance",
          description: "Ensure compliance with industry-specific regulations and standards.",
          points: [
            "Automated compliance checks",
            "Industry-specific templates",
            "Compliance reporting automation"
          ]
        },
        {
          title: "Proactive anomaly detection",
          description: "Identify and respond to potential security threats before they become incidents.",
          points: [
            "ML-based anomaly detection",
            "Behavioral analysis",
            "Predictive risk scoring"
          ]
        },
        {
          title: "Regulatory requirement adherence",
          description: "Stay compliant with evolving regulatory requirements.",
          points: [
            "Real-time compliance monitoring",
            "Automated policy enforcement",
            "Regulatory update tracking"
          ]
        }
      ]
    }
  };

  const stages = [
    {
      icon: Search,
      title: "Where is my data?",
      description: "In the fast pacing digital world the data is growing at a light speed and its virtually impossible to keep track of all the data sources in IT landscape. Enterprises can become paralyzed because they simply don't know where to start to get a complete view of all their data sources.",
      details: [
        "Complete data source visibility",
        "IT landscape integration",
        "Real-time data tracking"
      ],
      color: "from-blue-500 to-blue-600",
      highlight: "bg-blue-50",
      learnMorePath: "/use-cases/data-discovery-and-classification"
    },
    {
      icon: Database,
      title: "What is my data?",
      description: "Identifying the data context and its sensitivity is crucial to protect the right data to avoid risk of exposure. Data types like PII data, Credit Card information, and identity access keys etc. should be treated with top sensitivity compared with other data types.",
      details: [
        "PII data identification",
        "Credit card information protection",
        "Identity access key management"
      ],
      color: "from-purple-500 to-purple-600",
      highlight: "bg-purple-50",
      learnMorePath: "/use-cases/data-discovery-and-classification"
    },
    {
      icon: Users,
      title: "Who has access to my data?",
      description : "Simply knowing who has access to your data is difficult when SaaS applications or even cloud instances are used outside the purview of the IT or Security team. Therefore, it is crucial to enforce proper guardrails to protect sensitive data from falling into the wrong hands.",
      details: [
        "SaaS application access control",
        "Cloud instance monitoring",
        "Security team oversight"
      ],
      color: "from-green-500 to-green-600",
      highlight: "bg-green-50",
      learnMorePath: "/use-cases/data-governance"
    },
    {
      icon: Shield,
      title: "Is my data properly protected?",
      description: "Every industry has its own guidelines to meet the industry specific compliance levels with the sensitive data. Complying with the regulatory requirements by proactively detecting any irregularities and/or anomalies with the sensitive data sources is key to sustain the business, especially in very tightly regulated industries.",
      details: [
        "Industry-specific compliance",
        "Proactive anomaly detection",
        "Regulatory requirement adherence"
      ],
      color: "from-red-500 to-red-600",
      highlight: "bg-red-50",
      learnMorePath: "/use-cases/data-compliance"
    }
  ];

  return (
    <div className="py-20 bg-white relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Hero Section */}
        <div className="relative min-h-[80vh] flex items-center justify-center mb-20">
          {/* Background with boxes - Updated with light blue boxes */}
          <div className="h-full w-full absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-white z-10 [mask-image:radial-gradient(transparent,white)] pointer-events-none" />
            <div className="absolute inset-0 w-full h-full bg-white">
              <div className="hidden md:block">
                <Boxes boxClassName="bg-blue-100/60" />
              </div>
            </div>
          </div>

          {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
            className="relative z-20 text-center max-w-4xl mx-auto px-4"
      >
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Cloud native Data Security platform
        </h2>
            <p className="text-xl md:text-2xl text-gray-600 mb-8">
              Designed to revolutionize the way your organization approaches Data Security
            </p>
            <p className="text-lg text-gray-500 max-w-3xl mx-auto mb-10">
              You can't protect what you can't see – this is exactly where ANCILE incorporates your entire IT landscape data sources protections and augments visibility so you can peacefully focus on your business, and not worry about data breachers anymore.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button 
                onClick={handleGetDemo}
                className="bg-gradient-primary hover:bg-gradient-secondary text-white px-8 py-4 rounded-lg font-semibold transition-all transform hover:scale-105 shadow-glow"
              >
                Get Started
              </button> 
            </div>
      </motion.div>
        </div>

        {/* Timeline */}
        <div className="space-y-24 md:space-y-16 relative">
          {/* Connecting Line */}
          <div className="absolute left-1/2 top-6 bottom-6 w-px bg-gray-200 transform -translate-x-1/2" />

          {stages.map((stage, index) => (
          <motion.div
            key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`relative ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} flex flex-col lg:flex-row items-start pt-6 gap-8 lg:gap-16`}
            >
              {/* Stage Number - positioned relative to the timeline */}
              <div className="absolute left-1/2 top-0 transform -translate-x-1/2 z-10 flex items-center justify-center">
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                >
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${stage.color} flex items-center justify-center shadow-lg`}>
                    <span className="text-white font-bold">{index + 1}</span>
                  </div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="flex-1 mt-6 lg:mt-0">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`p-3 rounded-xl ${stage.highlight}`}>
                      <stage.icon className={`w-6 h-6 text-${stage.color.split('-')[1]}-500`} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{stage.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-6">{stage.description}</p>
                  <div className="space-y-3 mb-6">
                    {stage.details.map((detail, idx) => (
              <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 * idx }}
                        className="flex items-center gap-3"
                      >
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{detail}</span>
                      </motion.div>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <HoverBorderGradient
                      containerClassName="rounded-full"
                      className="flex items-center space-x-2 text-sm bg-white text-black px-4 py-2 hover:bg-gray-50"
                      onClick={() => handleLearnMore(stage.learnMorePath)}
                    >
                      <span>Explore</span>
                      <ExternalLink className="w-4 h-4" />
                    </HoverBorderGradient>
                    <HoverBorderGradient
                      containerClassName="rounded-full" 
                      className="flex items-center space-x-2 text-sm bg-white text-black px-4 py-2 hover:bg-gray-50"
                      onClick={() => setSelectedStage(stage.title)}
                    >
                      <span>Details</span>
                      <Info className="w-4 h-4" />
                    </HoverBorderGradient>
                  </div>
              </motion.div>
            </div>

              {/* Spacer for timeline alignment */}
              <div className="flex-1 hidden lg:block" />
          </motion.div>
        ))}
      </div>

        {/* Details Modal */}
        <Modal
          isOpen={!!selectedStage}
          onClose={() => setSelectedStage(null)}
          title={selectedStage || ""}
        >
          {selectedStage && stageDetails[selectedStage].sections.map((section, index) => (
            <div key={index} className="mb-8 last:mb-0">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">{section.title}</h4>
              <p className="text-gray-600 mb-4">{section.description}</p>
              <ul className="space-y-3">
                {section.points.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Modal>
      </div>
    </div>
  );
};

export default SecurityStory; 